import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import api from '../services/api';
import {
    Plus,
    Search,
    Filter,
    Sparkles,
    User,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
    Play,
    Trash2,
    Edit,
    FolderOpen
} from 'lucide-react';
import Pagination from '../components/Pagination';
import './TestCases.css';

function TestCases() {
    const { currentWorkspace } = useWorkspace();

    const [testCases, setTestCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem('rova_limit_cases')) || 10);
    const [stats, setStats] = useState({
        total: 0,
        aiGenerated: 0,
        avgSuccessRate: 0
    });

    const loadTestCases = useCallback(async () => {
        if (!currentWorkspace?.id) return;

        try {
            setLoading(true);
            const response = await api.get('/tests/cases', {
                params: {
                    workspaceId: currentWorkspace.id,
                    page: currentPage,
                    limit: limit,
                    search: searchQuery,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    source: sourceFilter !== 'all' ? sourceFilter : undefined
                }
            });
            setTestCases(response.data.rows || []);
            setTotalCount(response.data.count || 0);
            setTotalPages(Math.ceil((response.data.count || 0) / limit));

            // Calculate stats
            const rows = response.data.rows || [];
            const aiCount = rows.filter(tc => tc.source === 'ai').length;
            const avgSuccess = rows.length > 0
                ? rows.reduce((sum, tc) => sum + (tc.successRate || 0), 0) / rows.length
                : 0;

            setStats({
                total: response.data.count || rows.length,
                aiGenerated: Math.round((aiCount / (rows.length || 1)) * 100),
                avgSuccessRate: avgSuccess.toFixed(1)
            });
        } catch (error) {
            console.error('Failed to load test cases:', error);
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id, currentPage, searchQuery, statusFilter, sourceFilter, limit]);

    useEffect(() => {
        loadTestCases();
    }, [loadTestCases]);

    const handleExecute = async (testCaseId) => {
        try {
            await api.post(`/tests/cases/${testCaseId}/execute`, {
                workspaceId: currentWorkspace.id
            });
            alert('Test case queued for execution!');
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to execute test case');
        }
    };

    const getSourceBadge = (source) => {
        if (source === 'ai') {
            return (
                <span className="source-badge ai">
                    <Sparkles size={12} />
                    AI Gen
                </span>
            );
        }
        return (
            <span className="source-badge manual">
                <User size={12} />
                Manual
            </span>
        );
    };

    const getSuccessRateColor = (rate) => {
        if (rate >= 80) return 'success';
        if (rate >= 50) return 'warning';
        return 'error';
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const now = new Date();
        const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    };

    return (
        <div className="test-cases-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Test Case Library</h1>
                    <p className="page-subtitle">Manage and organize your AI-generated mobile test suites.</p>
                </div>
                <Link to="/test-goals" className="btn btn-primary">
                    <Plus size={16} />
                    Create Test Case
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="stats-row">
                <div className="mini-stat-card">
                    <span className="mini-stat-label">TOTAL CASES</span>
                    <div className="mini-stat-value">
                        <FolderOpen size={18} className="mini-stat-icon" />
                        <span>{stats.total.toLocaleString()}</span>
                    </div>
                </div>
                <div className="mini-stat-card">
                    <span className="mini-stat-label">AI GENERATED</span>
                    <div className="mini-stat-value">
                        <Sparkles size={18} className="mini-stat-icon purple" />
                        <span>{stats.aiGenerated}%</span>
                        <span className="mini-stat-suffix">of total</span>
                    </div>
                </div>
                <div className="mini-stat-card">
                    <span className="mini-stat-label">AVG SUCCESS RATE</span>
                    <div className="mini-stat-value">
                        <CheckCircle size={18} className="mini-stat-icon success" />
                        <span>{stats.avgSuccessRate}%</span>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="filters-bar">
                <div className="search-filter">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Search by ID, name, or tag..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="filter-dropdowns">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">Status: All</option>
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                        <option value="archived">Archived</option>
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">Priority: All</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                    <select
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">Source: AI-Generated</option>
                        <option value="ai">AI Only</option>
                        <option value="manual">Manual Only</option>
                    </select>
                    <button className="filter-btn">
                        <Filter size={14} />
                    </button>
                </div>
            </div>

            {/* Test Cases Table */}
            <div className="test-cases-table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading test cases...</p>
                    </div>
                ) : testCases.length === 0 ? (
                    <div className="empty-state">
                        <FolderOpen size={48} className="empty-icon" />
                        <h3>No Test Cases Yet</h3>
                        <p>Execute test goals to automatically generate reusable test cases.</p>
                        <Link to="/test-goals" className="btn btn-primary">
                            <Plus size={16} />
                            Create Test Goal
                        </Link>
                    </div>
                ) : (
                    <>
                        <table className="test-cases-table">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" /></th>
                                    <th>TEST ID</th>
                                    <th>NAME & DESCRIPTION</th>
                                    <th>SOURCE</th>
                                    <th>TAGS</th>
                                    <th>STATS</th>
                                    <th>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testCases.map((testCase) => (
                                    <tr key={testCase.id}>
                                        <td><input type="checkbox" /></td>
                                        <td className="test-id">TC-{testCase.id.slice(-4).toUpperCase()}</td>
                                        <td className="test-info">
                                            <span className="test-name">{testCase.title}</span>
                                            <span className="test-desc">{testCase.description?.slice(0, 50)}...</span>
                                        </td>
                                        <td>{getSourceBadge(testCase.source || 'ai')}</td>
                                        <td className="tags-cell">
                                            {testCase.tags?.slice(0, 2).map((tag, i) => (
                                                <span key={i} className="tag-badge">{tag}</span>
                                            ))}
                                            {testCase.tags?.length > 2 && (
                                                <span className="tag-more">+{testCase.tags.length - 2}</span>
                                            )}
                                        </td>
                                        <td className="stats-cell">
                                            <span className={`success-rate ${getSuccessRateColor(testCase.successRate || 0)}`}>
                                                {testCase.successRate || 0}%
                                            </span>
                                            <span className="last-run">{formatTimeAgo(testCase.lastRunAt)}</span>
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="action-btn"
                                                onClick={() => handleExecute(testCase.id)}
                                                title="Run Test"
                                            >
                                                <Play size={14} />
                                            </button>
                                            <button className="action-btn" title="Edit">
                                                <Edit size={14} />
                                            </button>
                                            <button className="action-btn" title="More">
                                                <MoreVertical size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <Pagination
                            page={currentPage}
                            totalPages={totalPages}
                            limit={limit}
                            onPageChange={setCurrentPage}
                            onLimitChange={setLimit}
                            storageKey="rova_limit_cases"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default TestCases;
